<script setup lang="ts">
import { Form, Head, usePage } from "@inertiajs/vue3";
import {
  AlertTriangleIcon,
  BriefcaseIcon,
  HelpCircleIcon,
  MailIcon,
  UsersIcon,
} from "lucide-vue-next";
import { computed, ref, watch } from "vue";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SettingsLayout from "@/layouts/settings/layout";
import { settingsProfilePath } from "@/routes";
import type { DiscussionGroupSummary } from "@/types";
import { initialsFromName } from "@/utils/initials";

defineOptions({ inheritAttrs: false });

interface ProfilePayload {
  id: number;
  name: string;
  email: string;
  role_title?: string | null;
  public_bio?: string | null;
  profile_complete?: boolean | null;
  discussion_groups?: DiscussionGroupSummary[];
}

interface Props {
  profile: ProfilePayload;
  can_manage_group_memberships: boolean;
}

const props = defineProps<Props>();
const page = usePage<{ auth?: { user?: { email?: string } } }>();

const name = ref(props.profile.name || "");
const roleTitle = ref(props.profile.role_title || "");
const publicBio = ref((props.profile.public_bio || "").slice(0, 500));

watch(
  () => props.profile,
  (nextProfile) => {
    name.value = nextProfile.name || "";
    roleTitle.value = nextProfile.role_title || "";
    publicBio.value = (nextProfile.public_bio || "").slice(0, 500);
  },
  { deep: true },
);

const groups = computed(() => props.profile.discussion_groups || []);
const email = computed(
  () => props.profile.email || page.props.auth?.user?.email || "",
);

const profileFields = computed(() => [
  { label: "Nombre", complete: name.value.trim().length > 0 },
  { label: "Cargo", complete: roleTitle.value.trim().length > 0 },
  { label: "Mini bio", complete: publicBio.value.trim().length > 0 },
  { label: "Grupos", complete: groups.value.length > 0 },
]);

const completionCount = computed(
  () => profileFields.value.filter((field) => field.complete).length,
);
const completionPercentage = computed(() =>
  Math.round((completionCount.value / profileFields.value.length) * 100),
);
const isProfileIncomplete = computed(() => completionPercentage.value < 100);

const initials = computed(() => initialsFromName(name.value));

const handlePublicBioInput = (value: string | number) => {
  publicBio.value = String(value).slice(0, 500);
};
</script>

<template>
  <Head title="Perfil" />

  <main
    class="relative flex h-full min-h-0 w-full flex-col gap-3 bg-muted/50 p-3"
  >
    <SettingsLayout
      current="profile"
      title="Perfil público"
      description="Tu equipo usa esta info para encontrarte y mencionarte correctamente."
    >
      <Form
        :action="settingsProfilePath()"
        method="patch"
        #default="{ errors, processing, recentlySuccessful }"
      >
        <div class="space-y-6">
          <div
            class="flex items-start gap-4 rounded-xl border border-border/70 bg-muted/30 p-4"
          >
            <Avatar class="size-14">
              <AvatarFallback class="text-base font-semibold">
                {{ initials || "--" }}
              </AvatarFallback>
            </Avatar>

            <div class="min-w-0 flex-1 space-y-1">
              <p class="truncate text-sm font-semibold text-foreground">
                {{ name || "Sin nombre" }}
              </p>
              <p class="truncate text-xs text-muted-foreground">{{ email }}</p>
              <div
                class="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                <MailIcon class="size-3" />
                Visible en directorio interno
              </div>
            </div>
          </div>

          <Alert
            v-if="isProfileIncomplete"
            class="border-amber-300/70 bg-amber-100/50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-100"
          >
            <AlertTriangleIcon class="text-amber-600 dark:text-amber-300" />
            <AlertTitle>Tu perfil está incompleto</AlertTitle>
            <AlertDescription>
              <p>
                No bloquea acciones, pero ayuda a que te encuentren por keyword
                en el directorio.
              </p>
              <p class="text-xs">
                {{ completionCount }}/{{ profileFields.length }} campos
                completados ({{ completionPercentage }}%).
              </p>
            </AlertDescription>
          </Alert>

          <div class="grid gap-5">
            <div class="space-y-1.5">
              <Label for="profile-name">Nombre completo</Label>
              <Input
                id="profile-name"
                v-model="name"
                name="name"
                type="text"
                required
              />
              <p v-if="errors.name" class="text-xs text-destructive">
                {{ errors.name }}
              </p>
            </div>

            <div class="space-y-1.5">
              <div class="flex items-center gap-1.5">
                <BriefcaseIcon class="size-3.5 text-muted-foreground" />
                <Label for="profile-role-title">Cargo</Label>
              </div>
              <Input
                id="profile-role-title"
                v-model="roleTitle"
                name="role_title"
                type="text"
                placeholder="Ej: Director Comercial, Analista Financiero"
              />
              <p v-if="errors.role_title" class="text-xs text-destructive">
                {{ errors.role_title }}
              </p>
            </div>

            <div class="space-y-1.5">
              <div class="flex items-center gap-1.5">
                <HelpCircleIcon class="size-3.5 text-muted-foreground" />
                <Label for="profile-public-bio">Mini perfil público</Label>
              </div>
              <Textarea
                id="profile-public-bio"
                v-model="publicBio"
                name="public_bio"
                rows="4"
                placeholder="Contá brevemente qué hacés y cuándo conviene contactarte."
                class="min-h-24"
                @update:model-value="handlePublicBioInput"
              />
              <div class="flex items-center justify-between">
                <p v-if="errors.public_bio" class="text-xs text-destructive">
                  {{ errors.public_bio }}
                </p>
                <p class="ml-auto text-xs text-muted-foreground">
                  {{ publicBio.length }}/500
                </p>
              </div>
            </div>

            <div
              class="space-y-2 rounded-xl border border-border/70 bg-card/70 p-3.5"
            >
              <div class="flex items-center gap-1.5">
                <UsersIcon class="size-3.5 text-muted-foreground" />
                <p class="text-sm font-medium text-foreground">Tus grupos</p>
              </div>

              <div class="flex flex-wrap gap-1.5">
                <Badge
                  v-for="group in groups"
                  :key="`profile-group-${group.id}`"
                  variant="secondary"
                  class="rounded-full"
                >
                  @{{ group.name }}
                </Badge>
                <p
                  v-if="groups.length === 0"
                  class="text-xs italic text-muted-foreground"
                >
                  Sin grupos asignados.
                </p>
              </div>

              <p class="text-xs text-muted-foreground">
                {{
                  props.can_manage_group_memberships
                    ? "Como owner, puedes administrar pertenencia a grupos desde Grupos."
                    : "Solo owner puede modificar pertenencia a grupos."
                }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <Button type="submit" :disabled="processing">
              {{ processing ? "Guardando..." : "Guardar cambios" }}
            </Button>
            <p v-if="recentlySuccessful" class="text-sm text-muted-foreground">
              Guardado.
            </p>
          </div>
        </div>
      </Form>
    </SettingsLayout>
  </main>
</template>
